import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  PromptItem,
  AppLanguage,
  OfficeHoursMessage,
  SolutionSessionMessage,
  SearchedUser,
  APP_LANGUAGES,
} from '@backrs_ui/grow-ui/src/models/models';

import { initiateSession } from './coachLubav.thunk';
import { logout } from '../session/logoutThunk';

export type ActiveTool = Readonly<{
  tool_name: string;
  display_name: string;
  started_at: number;
}>;

type CoachLubavState = Readonly<{
  loading: boolean;
  streaming: boolean;
  sessionId?: number;
  sessionTitle: string | null;
  language: AppLanguage;
  coachingUser?: SearchedUser;
  officeHoursMessages: OfficeHoursMessage[];
  solutionSessionMessages: SolutionSessionMessage[];
  suggestedPrompts: PromptItem[];
  error?: string;
  initialMessageFetched: boolean;
  activeTools: ActiveTool[];
  pendingSelectorResponse: string | null;
}>;

export const initialState: CoachLubavState = {
  loading: true,
  streaming: false,
  sessionId: undefined,
  sessionTitle: null,
  language: APP_LANGUAGES.ENGLISH,
  officeHoursMessages: [],
  solutionSessionMessages: [],
  suggestedPrompts: [],
  initialMessageFetched: false,
  activeTools: [],
  pendingSelectorResponse: null,
};

export const coachLubavSlice = createSlice({
  name: 'coachLubav',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsStreaming: (state, action: PayloadAction<boolean>) => {
      state.streaming = action.payload;
    },
    setSessionTitle: (state, action: PayloadAction<string | null>) => {
      state.sessionTitle = action.payload;
    },
    setAppLanguage: (state, action: PayloadAction<AppLanguage>) => {
      state.language = action.payload;
    },
    setCoachingUser: (state, action: PayloadAction<SearchedUser | undefined>) => {
      state.coachingUser = action.payload;
    },
    addOfficeHoursMessage: (state, action: PayloadAction<OfficeHoursMessage>) => {
      state.officeHoursMessages = [...state.officeHoursMessages, action.payload];
    },
    setOfficeHoursMessages: (state, action: PayloadAction<OfficeHoursMessage[]>) => {
      state.officeHoursMessages = action.payload;
    },
    setSolutionSessionMessages: (state, action: PayloadAction<SolutionSessionMessage[]>) => {
      state.solutionSessionMessages = action.payload;
    },
    addSolutionSessionMessage: (state, action: PayloadAction<SolutionSessionMessage>) => {
      state.solutionSessionMessages = [...state.solutionSessionMessages, action.payload];
    },
    resetLubavMessages: (state) => {
      state.officeHoursMessages = [];
      state.solutionSessionMessages = [];
      state.coachingUser = undefined;
    },
    deleteLastMessage: (state) => {
      if (state.officeHoursMessages.length > 0) {
        state.officeHoursMessages = state.officeHoursMessages.slice(0, -1);
      }
    },
    updateLastMessage: (state, action: PayloadAction<Partial<OfficeHoursMessage>>) => {
      if (state.officeHoursMessages.length > 0) {
        const lastIndex = state.officeHoursMessages.length - 1;

        state.officeHoursMessages[lastIndex] = {
          ...state.officeHoursMessages[lastIndex],
          ...action.payload,
        };
      }
    },
    updateLastSolutionSessionMessage: (
      state,
      action: PayloadAction<Partial<SolutionSessionMessage>>,
    ) => {
      if (state.solutionSessionMessages.length > 0) {
        const lastIndex = state.solutionSessionMessages.length - 1;

        state.solutionSessionMessages[lastIndex] = {
          ...state.solutionSessionMessages[lastIndex],
          ...action.payload,
        };
      }
    },
    setSuggestedPrompts: (state, action: PayloadAction<PromptItem[]>) => {
      state.suggestedPrompts = action.payload;
    },
    resetSuggestedPrompts: (state) => {
      state.suggestedPrompts = [];
    },
    resetCurrentSession: (state) => {
      state.sessionId = undefined;
      state.sessionTitle = null;
      state.streaming = false;
      state.initialMessageFetched = false;
      state.activeTools = [];
    },
    resetSolutionSession: (state) => {
      state.sessionId = undefined;
      state.solutionSessionMessages = [];
      state.suggestedPrompts = [];
      state.streaming = false;
      state.activeTools = [];
    },
    setSessionId: (state, action: PayloadAction<number>) => {
      state.sessionId = action.payload;
    },
    setInitialMessageFetched: (state, action: PayloadAction<boolean>) => {
      state.initialMessageFetched = action.payload;
    },
    addActiveTool: (state, action: PayloadAction<{ tool_name: string; display_name: string }>) => {
      const { tool_name, display_name } = action.payload;

      if (!state.activeTools.some((tool) => tool.tool_name === tool_name)) {
        state.activeTools = [
          ...state.activeTools,
          {
            tool_name,
            display_name,
            started_at: Date.now(),
          },
        ];
      }
    },
    removeActiveTool: (state, action: PayloadAction<string>) => {
      state.activeTools = state.activeTools.filter((tool) => tool.tool_name !== action.payload);
    },
    clearActiveTools: (state) => {
      state.activeTools = [];
    },
    setPendingSelectorResponse: (state, action: PayloadAction<string | null>) => {
      state.pendingSelectorResponse = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // LOGOUT
      .addCase(logout, (state) => {
        state.sessionId = undefined;
        state.sessionTitle = null;
        state.language = initialState.language;
        state.loading = false;
        state.streaming = false;
        state.error = undefined;
        state.officeHoursMessages = [];
        state.solutionSessionMessages = [];
        state.coachingUser = undefined;
        state.suggestedPrompts = [];
        state.initialMessageFetched = false;
        state.activeTools = [];
        state.pendingSelectorResponse = null;
      })
      .addCase(initiateSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(initiateSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(
        initiateSession.fulfilled,
        (state, action: ReturnType<typeof initiateSession.fulfilled>) => {
          if (action.payload) {
            if (action.meta.arg.activeSession === action.payload.activeSession) {
              state.sessionId = action.payload.sessionId;
            }
          }

          state.loading = false;
        },
      );
  },
});

export const {
  setIsLoading,
  setIsStreaming,
  setAppLanguage,
  setCoachingUser,
  setOfficeHoursMessages,
  setSolutionSessionMessages,
  addOfficeHoursMessage,
  addSolutionSessionMessage,
  resetLubavMessages,
  deleteLastMessage,
  updateLastMessage,
  updateLastSolutionSessionMessage,
  setSuggestedPrompts,
  resetSuggestedPrompts,
  resetCurrentSession,
  resetSolutionSession,
  setSessionId,
  setSessionTitle,
  setInitialMessageFetched,
  addActiveTool,
  removeActiveTool,
  clearActiveTools,
  setPendingSelectorResponse,
} = coachLubavSlice.actions;

// Reducer
export const coachLubavReducer = coachLubavSlice.reducer;

// Selectors
// only add selectors when you need them to do some sort of logic. Otherwise, just use the state directly
